import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

const PAGE_SIZE_OPTIONS = [10, 20, 30, 60]

interface CatalogPaginationText {
  label: string
  previous: string
  next: string
  page: string
  perPage: string
  showing: string
  goToPage: string
}

interface CatalogPaginationProps {
  page: number
  pageSize: number
  totalPageCount: number
  isLoading?: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  text: CatalogPaginationText
  className?: string
}

export function CatalogPagination({
  page,
  pageSize,
  totalPageCount,
  isLoading = false,
  onPageChange,
  onPageSizeChange,
  text,
  className,
}: CatalogPaginationProps) {
  const hasPreviousPage = page > 1
  const hasNextPage = page < totalPageCount
  const pageNumbers = Array.from({ length: totalPageCount }, (_, index) => index + 1).filter(
    (pageNumber) =>
      pageNumber === 1 || Math.abs(pageNumber - page) <= 2 || pageNumber === totalPageCount
  )

  const pageControls = pageNumbers.reduce<(number | "ellipsis")[]>((controls, pageNumber) => {
    const previous = controls[controls.length - 1]
    if (typeof previous === "number" && pageNumber - previous > 1) {
      controls.push("ellipsis")
    }
    controls.push(pageNumber)
    return controls
  }, [])

  return (
    <footer
      data-purpose="pagination-controls"
      aria-label="Catalog pagination"
      className={cn(
        "flex flex-col items-center justify-between gap-4 border-t border-border/60 pt-8 sm:flex-row",
        className
      )}
    >
      <div className="flex max-w-full items-center overflow-x-auto rounded border border-border bg-background text-sm text-muted-foreground">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPreviousPage || isLoading}
          className="min-h-11 shrink-0 cursor-pointer border-r border-border px-4 py-2 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent"
        >
          <span aria-hidden="true">&larr;</span> {text.previous}
        </button>

        {pageControls.map((control, index) =>
          control === "ellipsis" ? (
            <span
              key={`ellipsis-${index}`}
              className="min-h-11 shrink-0 border-r border-border px-4 py-2"
              aria-hidden="true"
            >
              ...
            </span>
          ) : (
            <button
              key={control}
              type="button"
              onClick={() => onPageChange(control)}
              disabled={isLoading || control === page}
              aria-current={control === page ? "page" : undefined}
              className={cn(
                "min-h-11 min-w-11 shrink-0 cursor-pointer border-r border-border px-4 py-2 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed",
                control === page &&
                  "bg-primary text-primary-foreground hover:bg-primary disabled:opacity-100"
              )}
            >
              {control}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNextPage || isLoading}
          className="min-h-11 shrink-0 cursor-pointer px-4 py-2 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent"
        >
          {text.next} <span aria-hidden="true">&rarr;</span>
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-muted-foreground sm:justify-end">
        <div className="flex items-center gap-2">
          <span>{text.goToPage}:</span>
          <Select
            value={page.toString()}
            onValueChange={(value) => onPageChange(Number(value))}
            disabled={isLoading}
          >
            <SelectTrigger className="h-10 w-20 cursor-pointer rounded border-border bg-background pl-3 pr-8 text-sm focus:border-border focus:ring-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded border-border bg-card">
              {pageNumbers.map((pageNumber) => (
                <SelectItem key={pageNumber} value={pageNumber.toString()}>
                  {pageNumber}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span>{text.perPage}:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onPageSizeChange(Number(value))}
            disabled={isLoading}
          >
            <SelectTrigger className="h-10 w-20 cursor-pointer rounded border-border bg-background pl-3 pr-8 text-sm focus:border-border focus:ring-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded border-border bg-card">
              {PAGE_SIZE_OPTIONS.map((option) => (
                <SelectItem key={option} value={option.toString()}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </footer>
  )
}
